import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Plus, Trash2, Save, Star, Flame, Leaf, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LOGO } from "../../lib/images";
import {
  fetchMenu, fetchSpecials, fetchSettings,
  adminCreateCategory, adminDeleteCategory,
  adminCreateItem, adminUpdateItem, adminDeleteItem,
  adminCreateSpecial, adminUpdateSpecial, adminDeleteSpecial,
  adminSaveSettings, adminListContact, adminDeleteContact,
} from "../../lib/api";

const TABS = ["Menu", "Specials", "Hours & Contact", "Messages"];
const field = "w-full bg-hb-surface border border-hb-line text-hb-cream px-3 py-2 outline-none focus:border-hb-brass transition-colors text-sm";

const Toggle = ({ on, onClick, icon: Icon, label }) => (
  <button type="button" onClick={onClick} className={`inline-flex items-center gap-1 px-2 py-1 border text-[0.62rem] uppercase tracking-wider transition-colors ${on ? "bg-hb-brass text-hb-black border-hb-brass" : "border-hb-line text-hb-muted"}`}>
    <Icon size={12} /> {label}
  </button>
);

function ItemRow({ item, onSave, onDelete }) {
  const [it, setIt] = useState(item);
  const set = (k, v) => setIt((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-hb-line p-4 bg-hb-black/30" data-testid="admin-item-row">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <input className={`${field} md:col-span-2`} value={it.name} onChange={(e) => set("name", e.target.value)} placeholder="Item name" />
        <input className={`${field} md:col-span-3`} value={it.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description" />
        <input className={field} value={it.price} onChange={(e) => set("price", e.target.value)} placeholder="$0.00" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Toggle on={it.spicy} onClick={() => set("spicy", !it.spicy)} icon={Flame} label="Spicy" />
        <Toggle on={it.favorite} onClick={() => set("favorite", !it.favorite)} icon={Star} label="Favorite" />
        <Toggle on={it.gluten_conscious} onClick={() => set("gluten_conscious", !it.gluten_conscious)} icon={Leaf} label="GF" />
        <Toggle on={it.is_new} onClick={() => set("is_new", !it.is_new)} icon={Sparkles} label="New" />
        <div className="flex-1" />
        <button onClick={() => onSave(it)} className="btn-brass px-3 py-2 text-[0.62rem] uppercase tracking-wider inline-flex items-center gap-1" data-testid="admin-save-item"><Save size={13} /> Save</button>
        <button onClick={() => onDelete(it.id)} className="border border-hb-line text-red-400 px-3 py-2 hover:border-red-400 transition-colors" data-testid="admin-delete-item"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Menu");
  const [menu, setMenu] = useState({ categories: [] });
  const [specials, setSpecials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const reloadMenu = () => fetchMenu().then(setMenu);
  const reloadSpecials = () => fetchSpecials().then((d) => setSpecials(d.specials));
  const reloadSettings = () => fetchSettings().then(setSettings);
  const reloadMessages = () => adminListContact().then((d) => setMessages(d.submissions));

  useEffect(() => {
    if (user) { reloadMenu(); reloadSpecials(); reloadSettings(); reloadMessages(); }
  }, [user]);

  if (!user) return null;

  // Menu handlers
  const addCategory = async () => {
    const name = prompt("New category name?");
    if (!name) return;
    await adminCreateCategory({ name, order: menu.categories.length });
    reloadMenu();
  };
  const delCategory = async (id) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    await adminDeleteCategory(id); reloadMenu();
  };
  const addItem = async (catId) => {
    await adminCreateItem({ category_id: catId, name: "New Item", description: "", price: "" });
    reloadMenu();
  };
  const saveItem = async (it) => { await adminUpdateItem(it.id, it); toast.success("Item saved"); reloadMenu(); };
  const delItem = async (id) => { await adminDeleteItem(id); reloadMenu(); };

  // Specials
  const addSpecial = async () => { await adminCreateSpecial({ name: "New Special", description: "", price: "", active: true, order: specials.length }); reloadSpecials(); };
  const saveSpecial = async (sp) => { await adminUpdateSpecial(sp.id, sp); toast.success("Special saved"); reloadSpecials(); };
  const delSpecial = async (id) => { await adminDeleteSpecial(id); reloadSpecials(); };

  // Settings
  const saveSettings = async () => { await adminSaveSettings(settings); toast.success("Settings saved"); reloadSettings(); };
  const delMessage = async (id) => { await adminDeleteContact(id); reloadMessages(); };

  const logout = () => { signOut(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-hb-black text-hb-cream">
      <Toaster position="top-center" richColors />
      <header className="border-b border-hb-line sticky top-0 bg-hb-black/90 backdrop-blur-xl z-40">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Hackberry Home" className="h-10 w-10 object-contain logo-invert" />
            <div>
              <p className="font-serif text-xl leading-none">Kitchen Dashboard</p>
              <p className="eyebrow text-hb-brass text-[0.55rem]">{user.email}</p>
            </div>
          </div>
          <button onClick={logout} className="border border-hb-line px-4 py-2 eyebrow text-[0.62rem] flex items-center gap-2 hover:border-hb-brass transition-colors" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-2 overflow-x-auto no-scrollbar pb-3">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.split(" ")[0].toLowerCase()}`}
              className={`whitespace-nowrap px-4 py-2 eyebrow text-[0.62rem] border transition-colors ${tab === t ? "bg-hb-brass text-hb-black border-hb-brass" : "border-hb-line text-hb-muted hover:text-hb-cream"}`}>
              {t}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {tab === "Menu" && (
          <div className="space-y-8" data-testid="admin-menu-panel">
            <button onClick={addCategory} className="btn-brass px-4 py-2 eyebrow text-[0.62rem] inline-flex items-center gap-2"><Plus size={14} /> Add Category</button>
            {menu.categories.map((cat) => (
              <div key={cat.id} className="border border-hb-line p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-3xl">{cat.name}</h3>
                  <div className="flex gap-2">
                    <button onClick={() => addItem(cat.id)} className="border border-hb-line px-3 py-1.5 eyebrow text-[0.6rem] inline-flex items-center gap-1 hover:border-hb-brass transition-colors"><Plus size={12} /> Item</button>
                    <button onClick={() => delCategory(cat.id)} className="border border-hb-line text-red-400 px-3 py-1.5 hover:border-red-400 transition-colors"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="space-y-3">
                  {(cat.items || []).map((it) => <ItemRow key={it.id} item={it} onSave={saveItem} onDelete={delItem} />)}
                  {!(cat.items || []).length && <p className="text-hb-muted text-sm">No items yet.</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Specials" && (
          <div className="space-y-4" data-testid="admin-specials-panel">
            <button onClick={addSpecial} className="btn-brass px-4 py-2 eyebrow text-[0.62rem] inline-flex items-center gap-2"><Plus size={14} /> Add Special</button>
            {specials.map((sp) => <SpecialRow key={sp.id} sp={sp} onSave={saveSpecial} onDelete={delSpecial} />)}
            {!specials.length && <p className="text-hb-muted text-sm">No daily specials. Add one to show it near the top of the menu.</p>}
          </div>
        )}

        {tab === "Hours & Contact" && settings && (
          <div className="max-w-2xl space-y-8" data-testid="admin-settings-panel">
            <div>
              <h3 className="font-serif text-2xl mb-4">Contact Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[["phone", "Phone"], ["email", "Email"], ["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["address_line1", "Address"], ["city_state_zip", "City, State ZIP"], ["google_place_id", "Google Place ID (enables live reviews)"]].map(([k, label]) => (
                  <div key={k}>
                    <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">{label}</label>
                    <input className={field} value={settings[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} data-testid={`admin-setting-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-4">Restaurant Hours</h3>
              <div className="space-y-2">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-28 text-sm text-hb-muted">{day}</span>
                    <input className={field} value={(settings.hours || {})[day] || ""} onChange={(e) => setSettings((s) => ({ ...s, hours: { ...s.hours, [day]: e.target.value } }))} placeholder="e.g. 6:00 AM – 3:00 PM or Closed" />
                  </div>
                ))}
              </div>
            </div>
            <button onClick={saveSettings} className="btn-brass px-6 py-3 eyebrow inline-flex items-center gap-2" data-testid="admin-save-settings"><Save size={15} /> Save Settings</button>
          </div>
        )}

        {tab === "Messages" && (
          <div className="space-y-3" data-testid="admin-messages-panel">
            {messages.map((m) => (
              <div key={m.id} className="border border-hb-line p-5" data-testid="admin-message">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-xl">{m.name} <span className="text-hb-brass text-sm">· {m.reason}</span></p>
                    <p className="text-hb-muted text-sm">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                  </div>
                  <button onClick={() => delMessage(m.id)} className="border border-hb-line text-red-400 px-3 py-1.5 hover:border-red-400 transition-colors"><Trash2 size={13} /></button>
                </div>
                <p className="mt-3 text-hb-cream/90 text-sm leading-relaxed">{m.message}</p>
                <p className="mt-2 text-hb-mutedlight text-xs">{new Date(m.created_at).toLocaleString()}</p>
              </div>
            ))}
            {!messages.length && <p className="text-hb-muted text-sm">No messages yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function SpecialRow({ sp, onSave, onDelete }) {
  const [s, setS] = useState(sp);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-hb-line p-4 bg-hb-black/30" data-testid="admin-special-row">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <input className={`${field} md:col-span-2`} value={s.name} onChange={(e) => set("name", e.target.value)} placeholder="Special name" />
        <input className={`${field} md:col-span-3`} value={s.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" />
        <input className={field} value={s.price} onChange={(e) => set("price", e.target.value)} placeholder="$0.00" />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={() => set("active", !s.active)} className={`px-3 py-1.5 border text-[0.62rem] uppercase tracking-wider ${s.active ? "bg-hb-brass text-hb-black border-hb-brass" : "border-hb-line text-hb-muted"}`}>
          {s.active ? "Active" : "Hidden"}
        </button>
        <div className="flex-1" />
        <button onClick={() => onSave(s)} className="btn-brass px-3 py-2 text-[0.62rem] uppercase tracking-wider inline-flex items-center gap-1"><Save size={13} /> Save</button>
        <button onClick={() => onDelete(s.id)} className="border border-hb-line text-red-400 px-3 py-2 hover:border-red-400 transition-colors"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
